import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { usePlaceSearch, optionMain, optionSub } from "@/lib/placeSearch";

// Australian full street-address autocomplete (Google Places, OSM backup).
export function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Start typing an Australian address…", testid }) {
  const { search, resolve } = usePlaceSearch("address");
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const box = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQ(v);
    onChange && onChange(v);
    setOpen(true);
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        setResults(await search(v));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 400);
  };

  const pick = async (pr) => {
    setOpen(false);
    let r;
    try { r = await resolve(pr); } catch { return; }
    const street = r.street || (r.display || "").split(",")[0];
    setQ(street);
    onChange && onChange(street);
    onSelect && onSelect(r);
  };

  return (
    <div className="relative" ref={box}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid={testid} value={q} onChange={handleChange} onFocus={() => q.trim().length >= 3 && results.length > 0 && setOpen(true)} placeholder={placeholder} className="pl-9" autoComplete="off" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-kinetic" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-64 overflow-y-auto" data-testid={`${testid}-options`}>
          {results.map((r, i) => (
            <button key={r.place_id || `${r.lat},${r.lon},${i}`} type="button" onClick={() => pick(r)} data-testid={`${testid}-option-${i}`}
              aria-label={r.display || [r.street, r.suburb, r.state, r.postcode].filter(Boolean).join(", ")}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-start gap-2 border-b border-border/50 last:border-0">
              <MapPin className="h-3.5 w-3.5 text-kinetic shrink-0 mt-0.5" />
              <span className="flex flex-col">
                <span className="font-medium truncate">{optionMain(r)}</span>
                <span className="text-xs text-muted-foreground truncate">{optionSub(r)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
